<!--
=========================================================
* Vue Material Dashboard 2 PRO - v3.1.0
=========================================================

* Product Page: https://creative-tim.com/product/vue-material-dashboard-2-pro
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
-->

<template>
  <div class="app-container">
    <div class="background-overlay"></div>
    <global-overlay />

    <div
      v-if="showSidenav && !isPinned"
      class="sidebar-overlay"
      @click="closeSidebar"
    ></div>

    <sidenav
      v-if="showSidenav && loggedIn && isEncryptionInitialized"
      :custom_class="color"
      class="fixed-start"
    />
    <main class="main-content position-relative max-height-vh-100 h-100 overflow-x-hidden">
      <navbar
        v-if="showNavbar && loggedIn && isEncryptionInitialized"
        :class="[isNavFixed ? navbarFixed : '', isAbsolute ? absolute : '']"
        :color="isAbsolute ? 'opacity-8' : ''"
        :min-nav="navbarMinimize"
      />
      <router-view />
      <material-snackbar />
      <app-footer v-show="showFooter" />
    </main>
  </div>
</template>

<script>
import Sidenav from "@/components/layout/Sidenav";
import Navbar from "@/components/layout/Navbar.vue";
import AppFooter from "@/components/layout/Footer.vue";
import { mapMutations, mapState } from "vuex";
import { isAuthenticated } from "@/services/auth.service";
import MaterialSnackbar from '@/components/ui/MaterialSnackbar';
import GlobalOverlay from '@/components/layout/GlobalOverlay.vue';
import { cryptoService } from '@/services/crypto.service';
import { redirectToUnlock } from '@/utils/redirectToUnlock';

export default {
  name: "App",
  components: {
    Sidenav,
    Navbar,
    AppFooter,
    MaterialSnackbar,
    GlobalOverlay
  },
  computed: {
    ...mapState([
      "color",
      "isAbsolute",
      "isNavFixed",
      "navbarFixed",
      "absolute",
      "showSidenav",
      "showNavbar",
      "showFooter",
      "showConfig",
      "hideConfigButton",
      "isPinned"
    ]),
    loggedIn() {
      return isAuthenticated();
    },
    isEncryptionInitialized() {
      return this.$store.getters['encryption/isEncryptionInitialized'];
    },
    isEncryptionReady() {
      return this.isEncryptionInitialized && cryptoService.areKeysReady();
    }
  },
  watch: {
    isEncryptionReady(newVal, oldVal) {
      // Only redirect if we're going from ready to not ready, and we're not already on unlock/landing
      if (oldVal && !newVal && this.$route.path !== '/unlock' && this.$route.path !== '/landing') {
        // Use nextTick to avoid navigation conflicts
        this.$nextTick(() => {
          redirectToUnlock();
        });
      }
    }
  },
  beforeMount() {
    const sidenav = document.getElementsByClassName("g-sidenav-show")[0];
    if (window.innerWidth > 1200) {
      sidenav.classList.add("g-sidenav-pinned");
    }
  },
  methods: {
    ...mapMutations(["toggleConfigurator", "navbarMinimize"]),
    closeSidebar() {
      this.navbarMinimize();
    }
  },
};
</script>

<style>
body {
  background-image: url("~@/assets/img/light_bg.svg");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  height: 100vh;
  overflow-x: hidden;
  position: relative;
}

.background-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.75);
  pointer-events: none;  /* Makes sure it doesn't interfere with clicks */
  z-index: $zindex-background;  /* Stays behind all other content */
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.35);
  z-index: $zindex-sidebar-overlay; /* Should be below the sidebar but above content */
}

.sidenav {
  z-index: $zindex-sidenav;
}

.main-content {
  z-index: $zindex-main;
}

.navbar {
  z-index: $zindex-navbar;
}

.material-snackbar {
  z-index: $zindex-modal;
}
</style>
